import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Card, Table, Button, Modal, Form, Row, Col } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaUpload, FaDownload } from 'react-icons/fa';
import api from '../api/axiosConfig';
import './ReceivingPlannedManagement.css';
const XLSX = require('xlsx-js-style');
const API_BASE_URL = '/api/inbound';
const toDateInputValue = (date) => format(date, 'yyyy-MM-dd');
const displayDate = (value) => {
    if (!value)
        return '-';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'yyyy-MM-dd');
};
const formatInstructionLocation = (detail) => {
    const parts = [detail.location, detail.rackNo, detail.floor]
        .map((part) => String(part || '').trim())
        .filter(Boolean);
    return parts.length > 0 ? parts.join('-') : '-';
};
const sanitizeInstructionRemark = (remark) => String(remark || '')
    .replace(/\s*\*?\s*\((?:분할입고|遺꾪븷[^\)]*)\s*\d+\s*\/\s*\d+\)\s*/g, ' ')
    .replace(/\s*\*?\s*遺꾪븷[^\d]*\d+\s*\/\s*\d+\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/^\*$/, '')
    .trim();
const sanitizeDisplayRemark = (remark) => sanitizeInstructionRemark(remark) || '-';
const normalizeSpreadsheetHeader = (value) => String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[()\-_/]/g, '')
    .toLowerCase();
const getSpreadsheetValue = (row, keys) => {
    const normalizedHeaderMap = Object.keys(row).reduce((accumulator, key) => {
        const normalizedKey = normalizeSpreadsheetHeader(key);
        if (normalizedKey && !accumulator[normalizedKey]) {
            accumulator[normalizedKey] = key;
        }
        return accumulator;
    }, {});
    for (const key of keys) {
        const directValue = row[key];
        if (directValue !== undefined && directValue !== null && String(directValue).trim() !== '') {
            return directValue;
        }
        const mappedKey = normalizedHeaderMap[normalizeSpreadsheetHeader(key)];
        const value = mappedKey ? row[mappedKey] : undefined;
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return value;
        }
    }
    return '';
};
const buildSpreadsheetHeaderIndexMap = (headerRow) => headerRow.reduce((accumulator, value, index) => {
    const normalizedKey = normalizeSpreadsheetHeader(String(value || ''));
    if (normalizedKey && accumulator[normalizedKey] === undefined) {
        accumulator[normalizedKey] = index;
    }
    return accumulator;
}, {});
const getSpreadsheetRowValue = (row, headerIndexMap, keys) => {
    const safeHeaderIndexMap = headerIndexMap || {};
    for (const key of keys) {
        const index = safeHeaderIndexMap[normalizeSpreadsheetHeader(key)];
        if (index === undefined)
            continue;
        const value = row[index];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return value;
        }
    }
    return '';
};
const getSpreadsheetRowValueWithFallback = (row, headerIndexMap, keys, fallbackIndexes = []) => {
    const headerValue = getSpreadsheetRowValue(row, headerIndexMap, keys);
    if (headerValue !== undefined && headerValue !== null && String(headerValue).trim() !== '') {
        return headerValue;
    }
    for (const index of fallbackIndexes) {
        const fallbackValue = row[index];
        if (fallbackValue !== undefined && fallbackValue !== null && String(fallbackValue).trim() !== '') {
            return fallbackValue;
        }
    }
    return '';
};
const DETAIL_UPLOAD_FIELD_ALIASES = {
    itemCode: ['품목코드', '상품코드', '제품코드'],
    itemName: ['품목명', '상품명', '제품명'],
    quantity: ['수량', '입고수량'],
    packageUnit: ['포장단위', '단위'],
    location: ['위치', '구역', '존', 'zone'],
    rackNo: ['층', '랙', '랙번호', 'rack', 'rackno', 'level'],
    floor: ['칸', '칸번호', 'bin', 'binno', '층번호'],
    remark: ['비고', '메모', 'remark']
};
const INBOUND_UPLOAD_PACKAGE_UNIT_MAX_LENGTH = 20;
const sanitizeUploadedPackageUnit = (value) => {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
        return 'box';
    }
    return normalizedValue.length <= INBOUND_UPLOAD_PACKAGE_UNIT_MAX_LENGTH ? normalizedValue : 'box';
};
const isValidUploadedInboundDetail = (detail) => Boolean(String(detail.itemCode || '').trim())
    && Boolean(String(detail.itemName || '').trim())
    && Number(detail.quantity || 0) > 0;
const buildUploadedDetailLocation = (row, headerIndexMap) => {
    const explicitLocation = String(getSpreadsheetRowValue(row, headerIndexMap, DETAIL_UPLOAD_FIELD_ALIASES.location) || '').trim();
    const rackHeaderValue = String(getSpreadsheetRowValue(row, headerIndexMap, ['랙번호', '랙', 'rack', 'rackno']) || '').trim();
    const levelHeaderValue = String(getSpreadsheetRowValue(row, headerIndexMap, ['층번호', '층', 'level']) || '').trim();
    const binHeaderValue = String(getSpreadsheetRowValue(row, headerIndexMap, ['칸번호', '칸', 'bin', 'binno']) || '').trim();
    if (!explicitLocation && !rackHeaderValue && !levelHeaderValue && !binHeaderValue) {
        return {
            location: '',
            rackNo: '',
            floor: ''
        };
    }
    if (explicitLocation) {
        return {
            location: explicitLocation,
            rackNo: rackHeaderValue || levelHeaderValue,
            floor: binHeaderValue
        };
    }
    return {
        location: rackHeaderValue,
        rackNo: levelHeaderValue,
        floor: binHeaderValue
    };
};
const buildInboundInstructionRows = (master, detailRows) => {
    const rows = [
        ['물류창고 입고지시서', '', '', ''],
        [`컨테이너 번호: ${master.containerNumber || '-'}`, '', '물류팀:', '전산:'],
        ['상품코드', '품목명', '수량', '위치', '비고'],
    ];
    detailRows.forEach((detail) => {
        rows.push([
            detail.itemCode || '',
            detail.itemName || '',
            Number(detail.quantity) || 0,
            formatInstructionLocation(detail),
            sanitizeInstructionRemark(detail.remark),
        ]);
    });
    if (detailRows.length === 0) {
        rows.push(['', '', '', '', '']);
    }
    return rows;
};
const TEMP_MASTER_STATUSES = new Set(['입고대기', '예정']);
const getMasterBoundStatus = (masterStatus, fallbackStatus = '입고대기') => {
    const normalized = String(masterStatus || '').trim();
    return normalized || fallbackStatus;
};
const isTempMasterStatus = (masterStatus) => TEMP_MASTER_STATUSES.has(getMasterBoundStatus(masterStatus));
const ReceivingPlannedManagement = () => {
    const [masters, setMasters] = useState([]);
    const [details, setDetails] = useState([]);
    const [selectedMaster, setSelectedMaster] = useState(null);
    const [selectedInboundNos, setSelectedInboundNos] = useState([]);
    const [selectedDetailNos, setSelectedDetailNos] = useState([]);
    const [detailDeleteTargetNos, setDetailDeleteTargetNos] = useState([]);
    const [showMasterModal, setShowMasterModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showInstruction, setShowInstruction] = useState(false);
    const [showDetailDeleteConfirm, setShowDetailDeleteConfirm] = useState(false);
    const [currentMaster, setCurrentMaster] = useState({});
    const [currentDetail, setCurrentDetail] = useState({});
    const [instruction, setInstruction] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [dateField, setDateField] = useState('createDate');
    const [searchType, setSearchType] = useState('inboundNo');
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState(() => toDateInputValue(new Date(new Date().setMonth(new Date().getMonth() - 1))));
    const [endDate, setEndDate] = useState(() => toDateInputValue(new Date()));
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [detailsPage, setDetailsPage] = useState(1);
    const [detailsPerPage, setDetailsPerPage] = useState(10);
    const showToast = (message) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(null), 3000);
    };
    const fetchMasters = async () => {
        try {
            const response = await api.get(`${API_BASE_URL}/masters`);
            const rows = response.data;
            setMasters(rows);
            setSelectedMaster((prev) => {
                if (!prev)
                    return null;
                return rows.find((master) => master.inboundNo === prev.inboundNo) || prev;
            });
        }
        catch (error) {
            console.error('Error fetching masters:', error);
        }
    };
    const fetchDetails = async (inboundNo, masterStatusOverride) => {
        var _a;
        try {
            const masterStatus = getMasterBoundStatus(masterStatusOverride ||
                (selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.status) ||
                ((_a = masters.find((master) => master.inboundNo === inboundNo)) === null || _a === void 0 ? void 0 : _a.status));
            const detailPath = masterStatus === '입고대기' || masterStatus === '예정'
                ? `/masters/${inboundNo}/details/temp`
                : `/masters/${inboundNo}/details`;
            const response = await api.get(`${API_BASE_URL}${detailPath}`);
            let resolvedDetails = response.data;
            if (resolvedDetails.length === 0) {
                const fallbackPath = isTempMasterStatus(masterStatus)
                    ? `/masters/${inboundNo}/details`
                    : `/masters/${inboundNo}/details/temp`;
                const fallbackResponse = await api.get(`${API_BASE_URL}${fallbackPath}`);
                resolvedDetails = fallbackResponse.data;
            }
            setDetails(resolvedDetails.map((detail) => ({
                ...detail,
                status: masterStatus
            })));
        }
        catch (error) {
            console.error('Error fetching details:', error);
            setDetails([]);
        }
    };
    useEffect(() => {
        fetchMasters();
    }, []);
    useEffect(() => {
        if (selectedMaster) {
            fetchDetails(selectedMaster.inboundNo, selectedMaster.status);
        }
        else {
            setDetails([]);
        }
    }, [selectedMaster]);
    const filteredMasters = useMemo(() => {
        return masters.filter((master) => {
            const selectedDateValue = master[dateField];
            if (selectedDateValue) {
                const normalizedDate = displayDate(selectedDateValue);
                if (startDate && normalizedDate < startDate)
                    return false;
                if (endDate && normalizedDate > endDate)
                    return false;
            }
            const keyword = searchTerm.trim().toLowerCase();
            if (!keyword)
                return true;
            if (searchType === 'all') {
                return [
                    master.inboundNo,
                    master.carrier,
                    master.status,
                    master.mainProduct,
                    master.containerNumber || ''
                ]
                    .join(' ')
                    .toLowerCase()
                    .includes(keyword);
            }
            return String(master[searchType] || '').toLowerCase().includes(keyword);
        });
    }, [dateField, endDate, masters, searchTerm, searchType, startDate]);
    const mastersTotalPages = itemsPerPage === -1 ? 1 : Math.max(1, Math.ceil(filteredMasters.length / itemsPerPage));
    const currentMasters = itemsPerPage === -1 ? filteredMasters : filteredMasters.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const detailsTotalPages = detailsPerPage === -1 ? 1 : Math.max(1, Math.ceil(details.length / detailsPerPage));
    const currentDetails = detailsPerPage === -1 ? details : details.slice((detailsPage - 1) * detailsPerPage, detailsPage * detailsPerPage);
    useEffect(() => {
        if (currentPage > mastersTotalPages) {
            setCurrentPage(mastersTotalPages);
        }
    }, [currentPage, mastersTotalPages]);
    useEffect(() => {
        if (detailsPage > detailsTotalPages) {
            setDetailsPage(detailsTotalPages);
        }
    }, [detailsPage, detailsTotalPages]);
    useEffect(() => {
        setSelectedDetailNos((prev) => prev.filter((detailNo) => details.some((detail) => detail.detailNo === detailNo)));
    }, [details]);
    useEffect(() => {
        setSelectedDetailNos([]);
        setDetailDeleteTargetNos([]);
    }, [selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo]);
    const handlePeriodSelect = (period) => {
        const today = new Date();
        const start = new Date(today);
        if (period === '1week')
            start.setDate(today.getDate() - 7);
        if (period === '1month')
            start.setMonth(today.getMonth() - 1);
        if (period === '6month')
            start.setMonth(today.getMonth() - 6);
        setStartDate(toDateInputValue(start));
        setEndDate(toDateInputValue(today));
        setCurrentPage(1);
    };
    const handleSearch = () => {
        setCurrentPage(1);
    };
    const clearFilters = () => {
        setDateField('createDate');
        setSearchType('inboundNo');
        setSearchTerm('');
        setStartDate(toDateInputValue(new Date(new Date().setMonth(new Date().getMonth() - 1))));
        setEndDate(toDateInputValue(new Date()));
        setCurrentPage(1);
    };
    const handleMasterSelect = (master) => {
        setSelectedMaster(master);
        setCurrentMaster(master);
    };
    const handleMasterCheckboxChange = (inboundNo) => {
        setSelectedInboundNos((prev) => prev.includes(inboundNo) ? prev.filter((item) => item !== inboundNo) : [...prev, inboundNo]);
    };
    const handleSelectAllMasters = () => {
        const visibleInboundNos = currentMasters.map((master) => master.inboundNo);
        const allSelected = visibleInboundNos.length > 0 &&
            visibleInboundNos.every((inboundNo) => selectedInboundNos.includes(inboundNo));
        setSelectedInboundNos((prev) => {
            if (allSelected) {
                return prev.filter((inboundNo) => !visibleInboundNos.includes(inboundNo));
            }
            return Array.from(new Set([...prev, ...visibleInboundNos]));
        });
    };
    const handleDetailCheckboxChange = (detailNo) => {
        setSelectedDetailNos((prev) => prev.includes(detailNo) ? prev.filter((item) => item !== detailNo) : [...prev, detailNo]);
    };
    const handleSelectAllDetails = () => {
        const visibleDetailNos = currentDetails.map((detail) => detail.detailNo);
        const allSelected = visibleDetailNos.length > 0 &&
            visibleDetailNos.every((detailNo) => selectedDetailNos.includes(detailNo));
        setSelectedDetailNos((prev) => {
            if (allSelected) {
                return prev.filter((detailNo) => !visibleDetailNos.includes(detailNo));
            }
            return Array.from(new Set([...prev, ...visibleDetailNos]));
        });
    };
    const openDeleteDetailConfirm = (detailNos) => {
        if (detailNos.length === 0) {
            alert('삭제할 상세 항목을 선택해주세요.');
            return;
        }
        setSelectedDetailNos(Array.from(new Set(detailNos)));
        setDetailDeleteTargetNos(Array.from(new Set(detailNos)));
        setShowDetailDeleteConfirm(true);
    };
    const handleMasterSubmit = async () => {
        try {
            if (currentMaster.inboundNo) {
                await api.post(`${API_BASE_URL}/masters/edit`, currentMaster);
            }
            else {
                await api.post(`${API_BASE_URL}/masters`, currentMaster);
            }
            await fetchMasters();
            setShowMasterModal(false);
            setCurrentMaster({});
            showToast(currentMaster.inboundNo ? '입고 예정이 수정되었습니다.' : '입고 예정이 등록되었습니다.');
        }
        catch (error) {
            console.error('Error saving master:', error);
            alert('입고 예정 저장 중 오류가 발생했습니다.');
        }
    };
    const handleDeleteMaster = async (inboundNo) => {
        if (!window.confirm(`입고번호 ${inboundNo}를 삭제하시겠습니까?`))
            return;
        try {
            await api.post(`${API_BASE_URL}/masters/delete/${inboundNo}`);
            if ((selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo) === inboundNo) {
                setSelectedMaster(null);
                setDetails([]);
            }
            setSelectedInboundNos((prev) => prev.filter((item) => item !== inboundNo));
            await fetchMasters();
        }
        catch (error) {
            console.error('Error deleting master:', error);
            alert('입고 예정 삭제 중 오류가 발생했습니다.');
        }
    };
    const handleDetailSubmit = async () => {
        if (!(selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo)) {
            alert('입고번호를 먼저 선택해주세요.');
            return;
        }
        try {
            const payload = {
                ...currentDetail,
                inboundNo: selectedMaster.inboundNo,
                status: getMasterBoundStatus(selectedMaster.status)
            };
            if (currentDetail.detailNo) {
                await api.post(`${API_BASE_URL}/details/edit`, payload);
            }
            else {
                const createPath = selectedMaster.status === '입고대기' || selectedMaster.status === '예정'
                    ? `/masters/${selectedMaster.inboundNo}/details/temp`
                    : `/masters/${selectedMaster.inboundNo}/details`;
                await api.post(`${API_BASE_URL}${createPath}`, payload);
            }
            await fetchDetails(selectedMaster.inboundNo, selectedMaster.status);
            setShowDetailModal(false);
            setCurrentDetail({});
            showToast(currentDetail.detailNo ? '상세 정보가 수정되었습니다.' : '상세 정보가 등록되었습니다.');
        }
        catch (error) {
            console.error('Error saving detail:', error);
            alert('상세 정보 저장 중 오류가 발생했습니다.');
        }
    };
    const handleDeleteDetail = async (detailNo) => {
        if (!window.confirm('선택한 상세 항목을 삭제하시겠습니까?'))
            return;
        try {
            const deletePath = (selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.status) === '입고대기' || (selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.status) === '예정'
                ? `/details/temp/delete/${detailNo}`
                : `/details/delete/${detailNo}`;
            await api.post(`${API_BASE_URL}${deletePath}`);
            if (selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo) {
                await fetchDetails(selectedMaster.inboundNo, selectedMaster.status);
            }
        }
        catch (error) {
            console.error('Error deleting detail:', error);
            alert('상세 항목 삭제 중 오류가 발생했습니다.');
        }
    };
    const handleDeleteSelectedDetails = async () => {
        if (detailDeleteTargetNos.length === 0) {
            setShowDetailDeleteConfirm(false);
            return;
        }
        try {
            const resolvedDeleteBasePath = isTempMasterStatus(selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.status)
                ? '/details/temp/delete/'
                : '/details/delete/';
            await Promise.all(detailDeleteTargetNos.map((detailNo) => api.post(`${API_BASE_URL}${resolvedDeleteBasePath}${detailNo}`)));
            if (selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo) {
                await fetchDetails(selectedMaster.inboundNo, selectedMaster.status);
            }
            setSelectedDetailNos((prev) => prev.filter((detailNo) => !detailDeleteTargetNos.includes(detailNo)));
            setDetailDeleteTargetNos([]);
            setShowDetailDeleteConfirm(false);
            showToast('선택한 상세 항목을 삭제했습니다.');
        }
        catch (error) {
            console.error('Error deleting selected details:', error);
            alert('선택한 상세 항목 삭제 중 오류가 발생했습니다.');
        }
    };
    const handleCompleteInbound = async () => {
        var _a, _b;
        if (selectedInboundNos.length === 0) {
            alert('입고완료 처리할 항목을 선택해주세요.');
            return;
        }
        if (!window.confirm(`선택된 ${selectedInboundNos.length}개 항목을 입고완료 처리하시겠습니까?`)) {
            return;
        }
        try {
            const response = await api.post(`${API_BASE_URL}/complete`, {
                inboundNos: selectedInboundNos
            });
            if (!((_a = response.data) === null || _a === void 0 ? void 0 : _a.success)) {
                alert(`입고완료 처리 중 오류가 발생했습니다: ${((_b = response.data) === null || _b === void 0 ? void 0 : _b.message) || '알 수 없는 오류'}`);
                return;
            }
            await fetchMasters();
            if (selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo) {
                setSelectedMaster({
                    ...selectedMaster,
                    status: '입고완료'
                });
                await fetchDetails(selectedMaster.inboundNo, '입고완료');
            }
            setSelectedInboundNos([]);
            alert('입고완료 처리가 완료되었습니다.');
        }
        catch (error) {
            console.error('입고완료 처리 오류:', error);
            alert('입고완료 처리 중 오류가 발생했습니다.');
        }
    };
    const handleLocationAssignment = async () => {
        if (selectedInboundNos.length === 0) {
            alert('로케이션배정 처리할 항목을 선택해주세요.');
            return;
        }
        if (!window.confirm(`선택된 ${selectedInboundNos.length}개 항목에 로케이션을 배정하시겠습니까?`)) {
            return;
        }
        try {
            for (const inboundNo of selectedInboundNos) {
                await api.post(`${API_BASE_URL}/masters2/${inboundNo}/details/add`);
                await api.post(`${API_BASE_URL}/masters/${inboundNo}/processing`);
            }
            await fetchMasters();
            if (selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo) {
                setSelectedMaster({
                    ...selectedMaster,
                    status: '입고처리중'
                });
                await fetchDetails(selectedMaster.inboundNo, '입고처리중');
            }
            setSelectedInboundNos([]);
            alert('로케이션배정 및 상태 업데이트가 완료되었습니다.');
        }
        catch (error) {
            console.error('로케이션배정 처리 오류:', error);
            alert('로케이션배정 처리 중 오류가 발생했습니다.');
        }
    };
    const handleShowInstruction = () => {
        if (!selectedMaster) {
            alert('입고번호를 먼저 선택해주세요.');
            return;
        }
        setInstruction({
            master: selectedMaster,
            details
        });
        setShowInstruction(true);
    };
    const handleDownloadAll = () => {
        const excelData = filteredMasters.map((master) => ({
            입고번호: master.inboundNo,
            입고예정일: displayDate(master.inboundDate),
            운송사: master.carrier,
            상태: master.status,
            주요품목: master.mainProduct,
            콘테이너번호: master.containerNumber || '',
            실제입고일: displayDate(master.actualInboundDate)
        }));
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '입고예정목록');
        XLSX.writeFile(workbook, '입고예정목록.xlsx');
    };
    const handleDownloadExcelTemplate = () => {
        const template = [
            {
                품목코드: 'YWD8025-GR',
                품목명: '9문수납장_그레이',
                포장단위: 'box',
                수량: 17,
                위치: 'A',
                랙번호: '1',
                층번호: '3',
                상태: '입고대기',
                비고: '인천항 입고'
            }
        ];
        const worksheet = XLSX.utils.json_to_sheet(template);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '입고상세_업로드양식');
        XLSX.writeFile(workbook, '입고상세_업로드양식.xlsx');
    };
    const handleDownloadNormalizedExcelTemplate = () => {
        const template = [
            {
                상품코드: 'YWD8025-GR',
                품목명: '9문수납장_그레이',
                포장단위: 'box',
                수량: 17,
                위치: 'A',
                층: '1',
                칸: '3',
                상태: '입고대기',
                비고: '인천쪽 입고'
            }
        ];
        const worksheet = XLSX.utils.json_to_sheet(template);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '입고상세_업로드양식');
        XLSX.writeFile(workbook, '입고상세_업로드양식.xlsx');
    };
    const handleDetailExcelDownload = () => {
        if (details.length === 0) {
            alert('다운로드할 상세 데이터가 없습니다.');
            return;
        }
        const excelData = details.map((detail) => ({
            품목코드: detail.itemCode,
            품목명: detail.itemName,
            수량: detail.quantity,
            포장단위: detail.packageUnit,
            위치: detail.location,
            랙번호: detail.rackNo,
            층번호: detail.floor,
            상태: detail.status,
            비고: detail.remark || ''
        }));
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '입고상세정보');
        XLSX.writeFile(workbook, `입고상세정보_${(selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo) || 'detail'}.xlsx`);
    };
    const handleInstructionExcelDownload = () => {
        if (!selectedMaster) {
            alert('입고번호를 먼저 선택해주세요.');
            return;
        }
        if (details.length === 0) {
            alert('다운로드할 상세 데이터가 없습니다.');
            return;
        }
        const sheetRows = buildInboundInstructionRows(selectedMaster, details);
        const allBorders = {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
        };
        const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
        worksheet['!cols'] = [
            { wch: 16 },
            { wch: 29 },
            { wch: 7 },
            { wch: 14 },
            { wch: 24 },
        ];
        worksheet['!rows'] = [
            { hpt: 24 },
            { hpt: 20 },
            { hpt: 22 },
            ...details.map(() => ({ hpt: 20 })),
        ];
        worksheet['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
        ];
        worksheet['!pageSetup'] = {
            paperSize: 9,
            orientation: 'portrait',
            fitToWidth: 1,
            fitToHeight: 0,
            horizontalCentered: true,
            verticalCentered: false,
        };
        worksheet['!margins'] = {
            left: 0.3,
            right: 0.3,
            top: 0.4,
            bottom: 0.4,
            header: 0.2,
            footer: 0.2,
        };
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:E3');
        for (let row = range.s.r; row <= range.e.r; row += 1) {
            for (let col = range.s.c; col <= range.e.c; col += 1) {
                const address = XLSX.utils.encode_cell({ r: row, c: col });
                if (!worksheet[address]) {
                    worksheet[address] = { t: 's', v: '' };
                }
                const isTitleRow = row === 0;
                const isHeaderRow = row === 2;
                const isDataRow = row >= 3;
                const isQuantityCol = col === 2;
                const isCodeCol = col === 0;
                const isLocationCol = col === 3;
                const isRemarkCol = col === 4;
                worksheet[address].s = {
                    font: {
                        sz: isTitleRow ? 16 : 11,
                        bold: isTitleRow || isHeaderRow,
                        name: 'Malgun Gothic',
                    },
                    alignment: {
                        horizontal: isTitleRow || isHeaderRow || isQuantityCol || isCodeCol || isLocationCol ? 'center' : (isRemarkCol ? 'left' : 'left'),
                        vertical: 'center',
                        wrapText: true,
                    },
                    border: allBorders,
                };
            }
        }
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '입고지시서');
        XLSX.writeFile(workbook, `입고지시서_${selectedMaster.inboundNo}.xlsx`);
    };
    const uploadDetailsInBatch = async (rows) => {
        try {
            if ((selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo) && isTempMasterStatus(selectedMaster.status)) {
                await Promise.all(rows.map((row) => api.post(`${API_BASE_URL}/masters/${selectedMaster.inboundNo}/details/temp`, {
                    itemCode: row.itemCode,
                    itemName: row.itemName,
                    quantity: row.quantity,
                    packageUnit: row.packageUnit,
                    location: row.location,
                    rackNo: row.rackNo,
                    floor: row.floor,
                    remark: row.remark,
                    uploadParserVersion: '20260521_1410'
                })));
            }
            else {
                await api.post(`${API_BASE_URL}/details/batch`, { details: rows });
            }
            if (selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo) {
                await fetchDetails(selectedMaster.inboundNo, selectedMaster.status);
            }
            showToast('상세 데이터가 업로드되었습니다.');
        }
        catch (error) {
            console.error('Error uploading details:', error);
            alert('상세 데이터 업로드 중 오류가 발생했습니다.');
        }
    };
    const handleFileUpload = (event) => {
        var _a;
        const file = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
        if (!file || !(selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo))
            return;
        const reader = new FileReader();
        reader.onload = async (loadEvent) => {
            var _a;
            const result = (_a = loadEvent.target) === null || _a === void 0 ? void 0 : _a.result;
            if (!result)
                return;
            const data = new Uint8Array(result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const sheetRows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });
            const [headerRow = [], ...dataRows] = sheetRows;
            const headerIndexMap = buildSpreadsheetHeaderIndexMap(headerRow);
            const syncedStatus = getMasterBoundStatus(selectedMaster.status);
            const uploadedDetails = dataRows
                .filter((row) => Array.isArray(row) && row.some((cell) => String(cell || '').trim() !== ''))
                .map((row, index) => {
                const resolvedLocation = buildUploadedDetailLocation(row, headerIndexMap);
                const rawPackageUnit = getSpreadsheetRowValue(row, headerIndexMap, [...DETAIL_UPLOAD_FIELD_ALIASES.packageUnit]);
                return {
                    detailNo: index + 1,
                    inboundNo: selectedMaster.inboundNo,
                    itemCode: String(getSpreadsheetRowValueWithFallback(row, headerIndexMap, [...DETAIL_UPLOAD_FIELD_ALIASES.itemCode], [0]) || ''),
                    itemName: String(getSpreadsheetRowValueWithFallback(row, headerIndexMap, [...DETAIL_UPLOAD_FIELD_ALIASES.itemName], [1]) || ''),
                    quantity: Number(getSpreadsheetRowValueWithFallback(row, headerIndexMap, [...DETAIL_UPLOAD_FIELD_ALIASES.quantity], [5]) || 0),
                    packageUnit: sanitizeUploadedPackageUnit(rawPackageUnit),
                    location: resolvedLocation.location,
                    rackNo: resolvedLocation.rackNo,
                    floor: resolvedLocation.floor,
                    status: syncedStatus,
                    remark: String(getSpreadsheetRowValueWithFallback(row, headerIndexMap, [...DETAIL_UPLOAD_FIELD_ALIASES.remark], [7]) || '')
                };
            })
                .filter(isValidUploadedInboundDetail);
            if (uploadedDetails.length === 0) {
                alert('유효한 데이터가 없습니다.');
                return;
            }
            if (window.confirm(`${uploadedDetails.length}개의 상세 항목을 업로드하시겠습니까?`)) {
                await uploadDetailsInBatch(uploadedDetails);
            }
        };
        reader.readAsArrayBuffer(file);
        event.target.value = '';
    };
    const masterRangeStart = filteredMasters.length === 0 ? 0 : itemsPerPage === -1 ? 1 : (currentPage - 1) * itemsPerPage + 1;
    const masterRangeEnd = itemsPerPage === -1 ? filteredMasters.length : Math.min(filteredMasters.length, currentPage * itemsPerPage);
    const detailRangeStart = details.length === 0 ? 0 : detailsPerPage === -1 ? 1 : (detailsPage - 1) * detailsPerPage + 1;
    const detailRangeEnd = detailsPerPage === -1 ? details.length : Math.min(details.length, detailsPage * detailsPerPage);
    return (_jsxs("div", { className: "container-fluid py-4", children: [_jsx("div", { className: "row", children: _jsxs("div", { className: "col-12", children: [_jsxs(Card, { className: "shadow-sm mb-4", children: [_jsx(Card.Header, { className: "bg-white py-3", children: _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsx("h5", { className: "mb-0", children: "\uC785\uACE0 \uC608\uC815 \uAD00\uB9AC" }), _jsxs(Button, { variant: "primary", className: "rounded-pill px-4", onClick: () => {
                                                    setCurrentMaster({});
                                                    setShowMasterModal(true);
                                                }, children: [_jsx(FaPlus, { className: "me-2" }), "\uC785\uACE0 \uC608\uC815 \uB4F1\uB85D"] })] }) }), _jsxs(Card.Body, { className: "p-3", children: [successMessage && (_jsxs("div", { className: "alert alert-success d-flex justify-content-between align-items-center py-2 mb-3", children: [_jsx("span", { children: successMessage }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Close", onClick: () => setSuccessMessage(null) })] })), _jsxs("div", { className: "d-flex align-items-center mb-3 flex-wrap", children: [_jsx("div", { className: "me-2 fw-bold", children: "\uAE30\uAC04:" }), _jsxs(Form.Select, { size: "sm", className: "me-2", style: { width: '120px' }, value: dateField, onChange: (e) => setDateField(e.target.value), children: [_jsx("option", { value: "createDate", children: "\uB4F1\uB85D\uC77C" }), _jsx("option", { value: "inboundDate", children: "\uC785\uACE0\uC608\uC815\uC77C" }), _jsx("option", { value: "actualInboundDate", children: "\uC2E4\uC81C\uC785\uACE0\uC77C" })] }), _jsx(Button, { variant: "outline-secondary", size: "sm", className: "me-1", onClick: () => handlePeriodSelect('today'), children: "\uC624\uB298" }), _jsx(Button, { variant: "outline-secondary", size: "sm", className: "me-1", onClick: () => handlePeriodSelect('1week'), children: "1\uC8FC\uC77C" }), _jsx(Button, { variant: "outline-secondary", size: "sm", className: "me-1", onClick: () => handlePeriodSelect('1month'), children: "1\uAC1C\uC6D4" }), _jsx(Button, { variant: "outline-secondary", size: "sm", className: "me-2", onClick: () => handlePeriodSelect('6month'), children: "6\uAC1C\uC6D4" }), _jsx(Form.Control, { type: "date", size: "sm", value: startDate, onChange: (e) => setStartDate(e.target.value), className: "me-1", style: { width: '140px' } }), _jsx("div", { className: "me-1", children: "~" }), _jsx(Form.Control, { type: "date", size: "sm", value: endDate, onChange: (e) => setEndDate(e.target.value), className: "me-3", style: { width: '140px' } })] }), _jsxs("div", { className: "d-flex align-items-center mb-3 flex-wrap", children: [_jsx("div", { className: "me-2 fw-bold", children: "\uAC80\uC0C9:" }), _jsx("div", { className: "me-2", children: _jsxs(Form.Select, { size: "sm", value: searchType, onChange: (e) => setSearchType(e.target.value), style: { width: '140px' }, children: [_jsx("option", { value: "all", children: "\uC804\uCCB4" }), _jsx("option", { value: "inboundNo", children: "\uC785\uACE0\uBC88\uD638" }), _jsx("option", { value: "carrier", children: "\uC6B4\uC1A1\uC0AC" }), _jsx("option", { value: "status", children: "\uC0C1\uD0DC" }), _jsx("option", { value: "mainProduct", children: "\uC8FC\uC694\uD488\uBAA9" }), _jsx("option", { value: "containerNumber", children: "\uCF58\uD14C\uC774\uB108\uBC88\uD638" })] }) }), _jsx(Form.Control, { type: "text", size: "sm", placeholder: searchType === 'all' ? '전체 검색어를 입력하세요' : '검색어를 입력하세요', value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), style: { width: '220px' }, className: "me-2" }), _jsx(Button, { variant: "primary", size: "sm", onClick: handleSearch, className: "me-1", children: "\uAC80\uC0C9" }), _jsx(Button, { variant: "outline-secondary", size: "sm", onClick: clearFilters, children: "\uCD08\uAE30\uD654" })] }), _jsxs("div", { className: "d-flex justify-content-between align-items-center mb-3", children: [_jsx("div", { children: _jsxs("span", { className: "me-2", children: ["\uC870\uD68C: ", filteredMasters.length, "\uAC74"] }) }), _jsxs("div", { children: [_jsx(Button, { variant: "outline-secondary", size: "sm", className: "me-2", onClick: handleDownloadAll, children: "\uC5D1\uC140 \uB2E4\uC6B4\uB85C\uB4DC" }), _jsx(Button, { variant: "outline-warning", size: "sm", className: "me-2", onClick: handleCompleteInbound, disabled: selectedInboundNos.length === 0, children: "\uC785\uACE0\uC644\uB8CC" }), _jsx(Button, { variant: "outline-primary", size: "sm", onClick: handleLocationAssignment, disabled: selectedInboundNos.length === 0, children: "\uB85C\uCF00\uC774\uC158\uBC30\uC815" })] })] }), _jsx("div", { className: "table-responsive", children: _jsxs(Table, { hover: true, className: "mb-0", children: [_jsx("thead", { className: "table-light", children: _jsxs("tr", { children: [_jsx("th", { style: { width: '40px' }, children: _jsx(Form.Check, { type: "checkbox", checked: currentMasters.length > 0 &&
                                                                            currentMasters.every((master) => selectedInboundNos.includes(master.inboundNo)), onChange: handleSelectAllMasters }) }), _jsx("th", { children: "\uC785\uACE0\uBC88\uD638" }), _jsx("th", { children: "\uC785\uACE0\uC608\uC815\uC77C" }), _jsx("th", { children: "\uC6B4\uC1A1\uC0AC" }), _jsx("th", { children: "\uC0C1\uD0DC" }), _jsx("th", { children: "\uC8FC\uC694\uD488\uBAA9" }), _jsx("th", { children: "\uCF58\uD14C\uC774\uB108\uBC88\uD638" }), _jsx("th", { children: "\uC2E4\uC81C\uC785\uACE0\uC77C" }), _jsx("th", { children: "\uC791\uC5C5" })] }) }), _jsx("tbody", { children: currentMasters.map((master) => (_jsxs("tr", { onClick: () => handleMasterSelect(master), style: { cursor: 'pointer' }, className: (selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.inboundNo) === master.inboundNo ? 'table-active' : '', children: [_jsx("td", { onClick: (e) => e.stopPropagation(), children: _jsx(Form.Check, { type: "checkbox", checked: selectedInboundNos.includes(master.inboundNo), onChange: () => handleMasterCheckboxChange(master.inboundNo) }) }), _jsx("td", { children: master.inboundNo }), _jsx("td", { children: displayDate(master.inboundDate) }), _jsx("td", { children: master.carrier }), _jsx("td", { children: master.status }), _jsx("td", { children: master.mainProduct }), _jsx("td", { children: master.containerNumber || '-' }), _jsx("td", { children: displayDate(master.actualInboundDate) }), _jsxs("td", { children: [_jsx(Button, { variant: "outline-primary", size: "sm", className: "me-2", onClick: (e) => {
                                                                                e.stopPropagation();
                                                                                setCurrentMaster(master);
                                                                                setShowMasterModal(true);
                                                                            }, children: _jsx(FaEdit, {}) }), _jsx(Button, { variant: "outline-danger", size: "sm", onClick: (e) => {
                                                                                e.stopPropagation();
                                                                                handleDeleteMaster(master.inboundNo);
                                                                            }, children: _jsx(FaTrash, {}) })] })] }, master.inboundNo))) })] }) })] }), _jsx(Card.Footer, { className: "bg-white border-0 py-3", children: _jsxs(Row, { className: "align-items-center", children: [_jsx(Col, { children: _jsxs(Form.Select, { style: { width: 'auto' }, size: "sm", value: itemsPerPage.toString(), onChange: (e) => {
                                                        setItemsPerPage(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }, children: [_jsx("option", { value: "-1", children: "\uC804\uCCB4 \uBCF4\uAE30" }), _jsx("option", { value: "5", children: "5\uAC1C\uC529 \uBCF4\uAE30" }), _jsx("option", { value: "10", children: "10\uAC1C\uC529 \uBCF4\uAE30" }), _jsx("option", { value: "20", children: "20\uAC1C\uC529 \uBCF4\uAE30" }), _jsx("option", { value: "50", children: "50\uAC1C\uC529 \uBCF4\uAE30" })] }) }), _jsx(Col, { className: "d-flex justify-content-center", children: _jsxs("div", { className: "d-flex align-items-center", children: [_jsx(Button, { variant: "outline-secondary", size: "sm", className: "me-1", disabled: currentPage === 1, onClick: () => setCurrentPage((prev) => Math.max(1, prev - 1)), children: "\uC774\uC804" }), Array.from({ length: mastersTotalPages }, (_, index) => index + 1)
                                                            .slice(Math.max(0, currentPage - 4), Math.max(5, currentPage + 3))
                                                            .map((pageNum) => (_jsx(Button, { variant: pageNum === currentPage ? 'primary' : 'outline-secondary', size: "sm", className: "me-1", onClick: () => setCurrentPage(pageNum), children: pageNum }, pageNum))), _jsx(Button, { variant: "outline-secondary", size: "sm", disabled: currentPage === mastersTotalPages, onClick: () => setCurrentPage((prev) => Math.min(mastersTotalPages, prev + 1)), children: "\uB2E4\uC74C" })] }) }), _jsx(Col, { className: "text-end", children: _jsxs("small", { className: "text-muted", children: ["\uCD1D ", filteredMasters.length, "\uAC74 \uC911 ", masterRangeStart, "-", masterRangeEnd, "\uAC74"] }) })] }) })] }), selectedMaster && (_jsxs(Card, { className: "shadow-sm", children: [_jsx(Card.Header, { className: "bg-white py-3", children: _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsx("h6", { className: "mb-0", children: "\uC785\uACE0 \uC0C1\uC138 \uC815\uBCF4" }), _jsxs("div", { className: "d-flex gap-2 flex-wrap detail-action-toolbar", children: [_jsx(Button, { variant: "dark", size: "sm", className: "rounded-pill px-3 fw-semibold shadow-sm", onClick: handleShowInstruction, children: "\uC785\uACE0 \uC9C0\uC2DC\uC11C" }), _jsxs(Button, { variant: "primary", size: "sm", className: "rounded-pill px-3 fw-semibold shadow-sm", onClick: () => { var _a; return (_a = document.getElementById('excelFileUpload')) === null || _a === void 0 ? void 0 : _a.click(); }, children: [_jsx(FaUpload, { className: "me-1" }), "\uC5D1\uC140 \uC5C5\uB85C\uB4DC"] }), _jsx(Button, { variant: "secondary", size: "sm", className: "rounded-pill px-3 fw-semibold shadow-sm", onClick: handleDownloadNormalizedExcelTemplate, children: "\uC5D1\uC140 \uC5C5\uB85C\uB4DC \uC591\uC2DD" }), _jsxs(Button, { variant: "success", size: "sm", className: "rounded-pill px-3 fw-semibold shadow-sm", onClick: handleInstructionExcelDownload, children: [_jsx(FaDownload, { className: "me-1" }), "\uC5D1\uC140 \uB2E4\uC6B4\uB85C\uB4DC"] }), selectedMaster.status !== '입고대기' && (_jsxs(Button, { variant: "primary", size: "sm", className: "rounded-pill px-3 fw-semibold shadow-sm", onClick: () => {
                                                            setCurrentDetail({
                                                                inboundNo: selectedMaster.inboundNo,
                                                                status: getMasterBoundStatus(selectedMaster.status)
                                                            });
                                                            setShowDetailModal(true);
                                                        }, children: [_jsx(FaPlus, { className: "me-1" }), "\uC0C1\uC138 \uB4F1\uB85D"] })), _jsxs(Button, { variant: "danger", size: "sm", className: "rounded-pill px-3 fw-semibold shadow-sm", onClick: () => openDeleteDetailConfirm(selectedDetailNos), disabled: selectedDetailNos.length === 0, children: [_jsx(FaTrash, { className: "me-1" }), "\uC120\uD0DD\uC0AD\uC81C"] }), _jsx(Form.Control, { id: "excelFileUpload", type: "file", accept: ".xlsx, .xls", style: { display: 'none' }, onChange: handleFileUpload })] })] }) }), _jsx(Card.Body, { children: _jsx("div", { className: "table-responsive", children: _jsxs(Table, { hover: true, className: "align-middle detail-table", children: [_jsx("thead", { className: "table-light", children: _jsxs("tr", { children: [_jsx("th", { style: { width: '44px' }, children: _jsx(Form.Check, { type: "checkbox", checked: currentDetails.length > 0 && currentDetails.every((detail) => selectedDetailNos.includes(detail.detailNo)), onChange: handleSelectAllDetails }) }), _jsx("th", { children: "\uD488\uBAA9\uCF54\uB4DC" }), _jsx("th", { children: "\uD488\uBAA9\uBA85" }), _jsx("th", { children: "\uC218\uB7C9" }), _jsx("th", { children: "\uD3EC\uC7A5\uB2E8\uC704" }), _jsx("th", { children: "\uC704\uCE58" }), _jsx("th", { children: "\uCE35" }), _jsx("th", { children: "\uCE78" }), _jsx("th", { children: "\uBE44\uACE0" }), _jsx("th", { children: "\uC0C1\uD0DC" }), _jsx("th", { children: "\uC791\uC5C5" })] }) }), _jsx("tbody", { children: currentDetails.map((detail) => (_jsxs("tr", { children: [_jsx("td", { children: _jsx(Form.Check, { type: "checkbox", checked: selectedDetailNos.includes(detail.detailNo), onChange: () => handleDetailCheckboxChange(detail.detailNo) }) }), _jsx("td", { children: detail.itemCode }), _jsx("td", { children: detail.itemName }), _jsx("td", { className: "text-end", children: detail.quantity }), _jsx("td", { className: "text-center", children: detail.packageUnit }), _jsx("td", { className: "text-center", children: detail.location }), _jsx("td", { className: "text-center", children: detail.rackNo }), _jsx("td", { className: "text-center", children: detail.floor }), _jsx("td", { children: sanitizeDisplayRemark(detail.remark) }), _jsx("td", { children: _jsx("span", { className: "badge rounded-pill bg-primary-subtle text-primary-emphasis border border-primary-subtle", children: getMasterBoundStatus(selectedMaster.status, detail.status) }) }), _jsxs("td", { className: "detail-row-actions", children: [_jsx(Button, { variant: "primary", size: "sm", className: "me-2 rounded-pill px-2 shadow-sm", onClick: () => {
                                                                            setCurrentDetail({
                                                                                ...detail,
                                                                                status: getMasterBoundStatus(selectedMaster.status, detail.status)
                                                                            });
                                                                            setShowDetailModal(true);
                                                                        }, children: _jsx(FaEdit, {}) }), _jsx(Button, { variant: "danger", size: "sm", className: "rounded-pill px-2 shadow-sm", onClick: () => openDeleteDetailConfirm([detail.detailNo]), children: _jsx(FaTrash, {}) })] })] }, detail.detailNo))) })] }) }) }), _jsx(Card.Footer, { className: "bg-white border-0 py-3", children: _jsxs(Row, { className: "align-items-center", children: [_jsxs(Col, { children: [_jsxs(Form.Select, { style: { width: 'auto' }, size: "sm", value: detailsPerPage.toString(), onChange: (e) => {
                                                            setDetailsPerPage(Number(e.target.value));
                                                            setDetailsPage(1);
                                                        }, children: [_jsx("option", { value: "-1", children: "\uC804\uCCB4 \uBCF4\uAE30" }), _jsx("option", { value: "10", children: "10\uAC1C\uC529 \uBCF4\uAE30" }), _jsx("option", { value: "20", children: "20\uAC1C\uC529 \uBCF4\uAE30" }), _jsx("option", { value: "50", children: "50\uAC1C\uC529 \uBCF4\uAE30" })] }), _jsx(Button, { variant: detailsPerPage === -1 ? 'primary' : 'outline-secondary', size: "sm", className: "ms-2 rounded-pill px-3", onClick: () => {
                                                            setDetailsPerPage(-1);
                                                            setDetailsPage(1);
                                                        }, children: '\uC804\uCCB4' })] }), _jsx(Col, { className: "d-flex justify-content-center", children: _jsxs("div", { className: "d-flex align-items-center", children: [_jsx(Button, { variant: "outline-secondary", size: "sm", className: "me-1", disabled: detailsPerPage === -1 || detailsPage === 1, onClick: () => setDetailsPage((prev) => Math.max(1, prev - 1)), children: "\uC774\uC804" }), detailsPerPage !== -1 &&
                                                            Array.from({ length: detailsTotalPages }, (_, index) => index + 1)
                                                                .slice(Math.max(0, detailsPage - 4), Math.max(5, detailsPage + 3))
                                                                .map((pageNum) => (_jsx(Button, { variant: pageNum === detailsPage ? 'primary' : 'outline-secondary', size: "sm", className: "me-1", onClick: () => setDetailsPage(pageNum), children: pageNum }, pageNum))), _jsx(Button, { variant: "outline-secondary", size: "sm", disabled: detailsPerPage === -1 || detailsPage === detailsTotalPages, onClick: () => setDetailsPage((prev) => Math.min(detailsTotalPages, prev + 1)), children: "\uB2E4\uC74C" })] }) }), _jsx(Col, { className: "text-end", children: _jsxs("small", { className: "text-muted", children: ["\uCD1D ", details.length, "\uAC74 \uC911 ", detailRangeStart, "-", detailRangeEnd, "\uAC74"] }) })] }) })] }))] }) }), _jsxs(Modal, { show: showMasterModal, onHide: () => setShowMasterModal(false), centered: true, backdrop: "static", keyboard: false, size: "lg", children: [_jsxs(Modal.Header, { className: "bg-light", children: [_jsx(Modal.Title, { className: "fw-bold", children: currentMaster.inboundNo ? '입고 예정 수정' : '입고 예정 등록' }), _jsx("button", { type: "button", className: "btn-close", onClick: () => setShowMasterModal(false), "aria-label": "Close" })] }), _jsx(Modal.Body, { children: _jsxs(Form, { children: [_jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uC785\uACE0\uBC88\uD638" }), _jsx(Form.Control, { type: "text", value: currentMaster.inboundNo || '', onChange: (e) => setCurrentMaster({ ...currentMaster, inboundNo: e.target.value }), placeholder: "\uC785\uACE0\uBC88\uD638\uB97C \uC785\uB825\uD558\uC138\uC694", disabled: Boolean(currentMaster.inboundNo) })] }), _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uC785\uACE0\uC608\uC815\uC77C" }), _jsx(Form.Control, { type: "date", value: currentMaster.inboundDate ? currentMaster.inboundDate.split('T')[0] : '', onChange: (e) => setCurrentMaster({ ...currentMaster, inboundDate: e.target.value }) })] }), _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uC6B4\uC1A1\uC0AC" }), _jsx(Form.Control, { type: "text", value: currentMaster.carrier || '', onChange: (e) => setCurrentMaster({ ...currentMaster, carrier: e.target.value }), placeholder: "\uC6B4\uC1A1\uC0AC\uBA85\uC744 \uC785\uB825\uD558\uC138\uC694" })] }), _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uC0C1\uD0DC" }), _jsxs(Form.Select, { value: currentMaster.status || '입고대기', onChange: (e) => setCurrentMaster({ ...currentMaster, status: e.target.value }), children: [_jsx("option", { value: "\uC785\uACE0\uB300\uAE30", children: "\uC785\uACE0\uB300\uAE30" }), _jsx("option", { value: "\uC785\uACE0\uCC98\uB9AC\uC911", children: "\uC785\uACE0\uCC98\uB9AC\uC911" }), _jsx("option", { value: "\uC785\uACE0\uC644\uB8CC", children: "\uC785\uACE0\uC644\uB8CC" }), _jsx("option", { value: "\uCDE8\uC18C", children: "\uCDE8\uC18C" })] })] }), _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uC8FC\uC694\uD488\uBAA9" }), _jsx(Form.Control, { type: "text", value: currentMaster.mainProduct || '', onChange: (e) => setCurrentMaster({ ...currentMaster, mainProduct: e.target.value }), placeholder: "\uC8FC\uC694\uD488\uBAA9\uC744 \uC785\uB825\uD558\uC138\uC694" })] }), _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uCF58\uD14C\uC774\uB108\uBC88\uD638" }), _jsx(Form.Control, { type: "text", value: currentMaster.containerNumber || '', onChange: (e) => setCurrentMaster({ ...currentMaster, containerNumber: e.target.value }), placeholder: "\uCF58\uD14C\uC774\uB108\uBC88\uD638\uB97C \uC785\uB825\uD558\uC138\uC694" })] }), _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uC2E4\uC81C\uC785\uACE0\uC77C" }), _jsx(Form.Control, { type: "date", value: currentMaster.actualInboundDate ? currentMaster.actualInboundDate.split('T')[0] : '', onChange: (e) => setCurrentMaster({ ...currentMaster, actualInboundDate: e.target.value }) })] })] }) }), _jsxs(Modal.Footer, { className: "bg-light", children: [_jsx(Button, { variant: "secondary", onClick: () => setShowMasterModal(false), children: "\uCDE8\uC18C" }), _jsx(Button, { variant: "primary", onClick: handleMasterSubmit, children: "\uC800\uC7A5" })] })] }), _jsxs(Modal, { show: showDetailModal, onHide: () => setShowDetailModal(false), centered: true, backdrop: "static", keyboard: false, size: "lg", children: [_jsxs(Modal.Header, { className: "bg-light", children: [_jsx(Modal.Title, { className: "fw-bold", children: currentDetail.detailNo ? '상세 수정' : '상세 등록' }), _jsx("button", { type: "button", className: "btn-close", onClick: () => setShowDetailModal(false), "aria-label": "Close" })] }), _jsx(Modal.Body, { children: _jsxs(Form, { children: [_jsxs(Row, { children: [_jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uD488\uBAA9\uCF54\uB4DC" }), _jsx(Form.Control, { type: "text", value: currentDetail.itemCode || '', onChange: (e) => setCurrentDetail({ ...currentDetail, itemCode: e.target.value }), placeholder: "\uD488\uBAA9\uCF54\uB4DC\uB97C \uC785\uB825\uD558\uC138\uC694" })] }) }), _jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uD488\uBAA9\uBA85" }), _jsx(Form.Control, { type: "text", value: currentDetail.itemName || '', onChange: (e) => setCurrentDetail({ ...currentDetail, itemName: e.target.value }), placeholder: "\uD488\uBAA9\uBA85\uC744 \uC785\uB825\uD558\uC138\uC694" })] }) })] }), _jsxs(Row, { children: [_jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uC218\uB7C9" }), _jsx(Form.Control, { type: "number", value: currentDetail.quantity || '', onChange: (e) => setCurrentDetail({
                                                            ...currentDetail,
                                                            quantity: e.target.value ? Number(e.target.value) : undefined
                                                        }), placeholder: "\uC218\uB7C9\uC744 \uC785\uB825\uD558\uC138\uC694" })] }) }), _jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uD3EC\uC7A5\uB2E8\uC704" }), _jsx(Form.Control, { type: "text", value: currentDetail.packageUnit || '', onChange: (e) => setCurrentDetail({ ...currentDetail, packageUnit: e.target.value }), placeholder: "\uD3EC\uC7A5\uB2E8\uC704\uB97C \uC785\uB825\uD558\uC138\uC694" })] }) })] }), _jsxs(Row, { children: [_jsx(Col, { md: 4, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uC704\uCE58" }), _jsx(Form.Control, { type: "text", value: currentDetail.location || '', onChange: (e) => setCurrentDetail({ ...currentDetail, location: e.target.value }), placeholder: "\uC704\uCE58\uB97C \uC785\uB825\uD558\uC138\uC694" })] }) }), _jsx(Col, { md: 4, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uCE35\uBC88\uD638" }), _jsx(Form.Control, { type: "text", value: currentDetail.rackNo || '', onChange: (e) => setCurrentDetail({ ...currentDetail, rackNo: e.target.value }), placeholder: "\uCE35\uBC88\uD638\uB97C \uC785\uB825\uD558\uC138\uC694" })] }) }), _jsx(Col, { md: 4, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uCE78\uBC88\uD638" }), _jsx(Form.Control, { type: "text", value: currentDetail.floor || '', onChange: (e) => setCurrentDetail({ ...currentDetail, floor: e.target.value }), placeholder: "\uCE78\uBC88\uD638\uB97C \uC785\uB825\uD558\uC138\uC694" })] }) })] }), _jsxs(Row, { children: [_jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uC0C1\uD0DC" }), _jsx(Form.Control, { type: "text", value: getMasterBoundStatus(selectedMaster === null || selectedMaster === void 0 ? void 0 : selectedMaster.status, currentDetail.status), readOnly: true, className: "bg-light" })] }) }), _jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { className: "fw-bold", children: "\uBE44\uACE0" }), _jsx(Form.Control, { type: "text", value: currentDetail.remark || '', onChange: (e) => setCurrentDetail({ ...currentDetail, remark: e.target.value }), placeholder: "\uBE44\uACE0\uB97C \uC785\uB825\uD558\uC138\uC694" })] }) })] })] }) }), _jsxs(Modal.Footer, { className: "bg-light", children: [_jsx(Button, { variant: "secondary", onClick: () => setShowDetailModal(false), children: "\uCDE8\uC18C" }), _jsx(Button, { variant: "primary", onClick: handleDetailSubmit, children: "\uC800\uC7A5" })] })] }), _jsxs(Modal, { show: showDetailDeleteConfirm, onHide: () => {
                    setShowDetailDeleteConfirm(false);
                    setDetailDeleteTargetNos([]);
                }, centered: true, size: "sm", dialogClassName: "detail-delete-confirm-modal", children: [_jsx(Modal.Header, { closeButton: true, children: _jsx(Modal.Title, { children: "\uC0C1\uC138 \uD56D\uBAA9 \uC0AD\uC81C \uD655\uC778" }) }), _jsxs(Modal.Body, { children: ["\uC120\uD0DD\uD55C \uC0C1\uC138 \uD56D\uBAA9 ", selectedDetailNos.length, "\uAC74\uC744 \uC0AD\uC81C\uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?", _jsx("div", { className: "text-muted small mt-2", children: "\uC0AD\uC81C \uD6C4\uC5D0\uB294 \uBCF5\uAD6C\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4." })] }), _jsxs(Modal.Footer, { children: [_jsx(Button, { variant: "secondary", onClick: () => {
                                    setShowDetailDeleteConfirm(false);
                                    setDetailDeleteTargetNos([]);
                                }, children: "\uCDE8\uC18C" }), _jsx(Button, { variant: "danger", onClick: handleDeleteSelectedDetails, children: "\uC0AD\uC81C" })] })] }), _jsxs(Modal, { show: showInstruction, onHide: () => setShowInstruction(false), size: "xl", centered: true, children: [_jsx(Modal.Header, { closeButton: true, children: _jsx(Modal.Title, { children: "\uC785\uACE0 \uC9C0\uC2DC\uC11C" }) }), _jsx(Modal.Body, { children: instruction && (_jsxs("div", { children: [_jsxs("div", { className: "mb-3", children: [_jsx("b", { children: "\uC785\uACE0\uBC88\uD638:" }), " ", instruction.master.inboundNo, " \u00A0", _jsx("b", { children: "\uCF58\uD14C\uC774\uB108\uBC88\uD638:" }), " ", instruction.master.containerNumber || '-', " \u00A0", _jsx("b", { children: "\uC6B4\uC1A1\uC0AC:" }), " ", instruction.master.carrier, " \u00A0", _jsx("b", { children: "\uC785\uACE0\uC608\uC815\uC77C:" }), " ", displayDate(instruction.master.inboundDate)] }), _jsxs(Table, { bordered: true, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "\uD488\uBC88" }), _jsx("th", { children: "\uD488\uBA85" }), _jsx("th", { children: "\uC0C9\uC0C1" }), _jsx("th", { children: "\uC218\uB7C9" }), _jsx("th", { children: "\uD655\uC778\uC218\uB7C9" }), _jsx("th", { children: "\uB300\uD45C\uB799" }), _jsx("th", { children: "\uBE44\uACE0" })] }) }), _jsx("tbody", { children: instruction.details.map((detail, index) => {
                                                var _a;
                                                return (_jsxs("tr", { children: [_jsx("td", { children: detail.itemCode }), _jsx("td", { children: detail.itemName }), _jsx("td", { children: detail.color || '' }), _jsx("td", { children: detail.quantity }), _jsx("td", { children: (_a = detail.confirmedQuantity) !== null && _a !== void 0 ? _a : '' }), _jsx("td", { children: formatInstructionLocation(detail) }), _jsx("td", { children: sanitizeInstructionRemark(detail.remark) })] }, `${detail.detailNo}-${index}`));
                                            }) })] })] })) })] })] }));
};
export default ReceivingPlannedManagement;
